// Skeleton — pulsing placeholder block. Building block for screen-level
// skeleton composites (StatsScreen.SkeletonGrid, HomeScreen.Operational
// Skeleton, per-screen composites, …). Each composite
// keeps its own layout (mirroring its loaded counterpart) but stops
// re-defining the pulse animation and the tinted-rectangle pattern.
//
// Two animation variants:
//   'pulse'      — gentle 1 → 0.5 → 1 (default; matches StatsScreen)
//   'pulse-deep' — deeper 0.6 → 0.2 → 0.6 (matches HomeScreen +
//                  the `cmpPulse` precedent)
//
// The keyframes are injected once per app bootstrap via
// <SkeletonStyles /> — render it at the top of any tree that uses
// <Skeleton>. Browsers de-dupe identical CSS rules, so multiple mounts
// are harmless.

import type { CSSProperties } from 'react';
import type { ThemeTokens } from '@nayto/theme';

export type SkeletonAnimation = 'pulse' | 'pulse-deep';

interface SkeletonProps {
  theme: ThemeTokens;
  width?: number | string;
  height?: number | string;
  borderRadius?: number;
  aspectRatio?: string;
  marginBottom?: number;
  /** Which keyframe to use. Default 'pulse'. */
  variant?: SkeletonAnimation;
  /** Optional staggered delay (in seconds). Useful for a row of
   * placeholders that wave one after the other. */
  delay?: number;
  /** Optional escape hatch for one-off styling. */
  style?: CSSProperties;
}

export function Skeleton({
  theme,
  width,
  height,
  borderRadius = 4,
  aspectRatio,
  marginBottom,
  variant = 'pulse',
  delay,
  style,
}: SkeletonProps) {
  const keyframe = variant === 'pulse-deep' ? 'naytoPulseDeep' : 'naytoPulse';
  const animation = `${keyframe} 1.4s ease-in-out infinite${
    typeof delay === 'number' ? ` ${delay}s` : ''
  }`;
  return (
    <div
      style={{
        width,
        height,
        borderRadius,
        aspectRatio,
        marginBottom,
        background: theme.tint,
        animation,
        ...style,
      }}
    />
  );
}

/** Keyframe definitions used by <Skeleton>. Mount once at the root of
 * any subtree that renders skeletons. Safe to mount multiple times. */
export function SkeletonStyles() {
  return (
    <style>{`
      @keyframes naytoPulse { 0%,100% { opacity: 1 } 50% { opacity: 0.5 } }
      @keyframes naytoPulseDeep { 0%,100% { opacity: 0.6 } 50% { opacity: 0.2 } }
    `}</style>
  );
}
